import React from 'react';
import { Navigate } from 'react-router-dom';
import { Server } from '../../server/server';
import AlertModal from '../modals/AlertModal';
import MessageModal from '../modals/MessageModal';
import PageEnvelope from '../elements/PageEnvelope';
import ContentPanel from '../elements/ContentPanel';
import PostPanel from '../elements/PostPanel';
import PageHeader from '../elements/PageHeader';
import './SitePage.css'
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { HiArrowUturnDown } from 'react-icons/hi2';
import QuestionModal from '../modals/QuestionModal';
import { getFirstParagraph } from '../util/util';

interface BlogPageState {
  blog: any;
  comments: any[];
  more: any;
  message: string;
  alert: string;
  question: string;
  navigate: string;
  date: string;
  header: string;
  sort: boolean;
  edit: string;
  loading: boolean;
  loadingMore: boolean;
}

class BlogPage extends React.Component<{}, BlogPageState> {
  constructor(props: any) {
    super(props);
    this.state = {
      blog: null,
      comments: [],
      more: null,
      message: '',
      alert: '',
      question: '',
      navigate: '',
      date: '',
      header: 'Loading...',
      sort: true,
      edit: '',
      loading: false,
      loadingMore: false
    }

    this.onLogin = this.onLogin.bind(this);
    this.onLogout = this.onLogout.bind(this);
  }

  componentDidMount() {
    Server.addEventListener('logout', this.onLogout);
    Server.addEventListener('login', this.onLogin);
    this.getBlog();
  }

  componentWillUnmount(): void {
    Server.removeEventListener('logout', this.onLogout);
    Server.removeEventListener('login', this.onLogin);
  }

  async getBlog() {
    let slug = window.location.pathname.substring(6);

    let blog = await Server.blogs.getBlogFromCacheBySlug(slug);

    // get blog from server
    if(!blog) {
      let response:any = await Server.blogs.getBlog({slug});

      if (!response.success) {
        this.setState({header: response.message});
        return;
      }

      blog = response.blog;

      await Server.blogs.setBlogInCache(blog);
    }

    await Server.public.loadProfiles([blog.author]);

    await this.loadComments(blog.id, this.state.sort);

    this.setState({blog});
  }

  async loadComments(blogId:string, sort:boolean) {
    let cache = await Server.blogs.getBlogCommentsFromCache(blogId, sort);
    
    let updating = false;
    if (cache) {
      let profiles = this.getProfilesInComments(cache.comments);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) {
        this.setState({loading: true});
        await Server.public.loadProfiles(profiles);
      }
  
      this.setState({comments: cache.comments, more: cache.more});

      if(sort && cache.more) 
        return;

      updating = cache.comments.length > 0;
      let refreshTimeItem = window.sessionStorage.getItem(`BlogCommentRefresh#${blogId}#${sort}`);
      if(refreshTimeItem) {
        let refreshTime = parseInt(refreshTimeItem);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) {
          this.setState({loading: false});
          return;
        }
      }
    }

    let params:any = {id: blogId, sort};
    if(updating) {
      let pi = sort ? cache.comments.length-1 : 1;
      params.after = {date: cache.comments[pi].date, user: cache.comments[pi].user};
    }

    let response = await Server.blogs.getBlogComments(params);

    if (!response.success) {
      this.setState({header: response.message, loading: false});
      return;
    }

    window.sessionStorage.setItem(`BlogCommentRefresh#${blogId}#${sort}`, new Date().getTime().toString());

    let newComments = response.comments;
    let more = response.more;

    let profiles = this.getProfilesInComments(newComments);
    await Server.public.loadProfiles(profiles);

    let comments = [];
    if(updating) 
      comments = this.addNewCommentsToExistingComments(newComments, this.state.comments, sort);
    else  
      comments = newComments;

    await Server.blogs.setBlogCommentsInCache(blogId, sort, {comments, more});

    this.setState({comments, more, loading: false});
  }

  async loadMoreComments() {
    this.setState({loadingMore: true});

    let response = await Server.blogs.getBlogComments({id: this.state.blog.id, sort: this.state.sort, more: this.state.more});

    if(!response.success) {
      this.setState({loadingMore: false, alert: response.message});
      return;
    }

    let newComments = response.comments;
    let more = response.more;

    let profiles = this.getProfilesInComments(newComments);
    await Server.public.loadProfiles(profiles);

    let comments = this.addNewCommentsToExistingComments(newComments, this.state.comments, this.state.sort);

    this.setState({comments, more, loadingMore: false});
  }

  getProfilesInComments(comments:any[]) {
    let profiles = [];
    for (let i = 0; i < comments.length; i++) {
      let comment = comments[i];
      if(profiles.indexOf(comment.user) == -1)
        profiles.push(comment.user);
    }
    return profiles;
  }

  addNewCommentsToExistingComments(newComments:any[], existingComments:any[], sort:boolean) {
    let comments = JSON.parse(JSON.stringify(existingComments));
    for(let i = 0; i < newComments.length; i++) {
      let exists = false;
      for(let j = 0; j < comments.length; j++) 
        if(newComments[i].date == comments[j].date && newComments[i].user == comments[j].user)
          exists = true;
      if(!exists)
        comments.push(newComments[i]);
    }
    this.sortComments(comments, sort);
    return comments;
  }

  sortComments(comments:any[], sort:boolean) {
    comments.sort((a:any, b:any)=>{
      if(a.date > b.date)
        return sort ? 1 : -1;
      else if(a.date < b.date)
        return sort ? -1 : 1;
      return 0;
    })
  }

  async onCreateComment(content:string) {
    if(content == null)
      return;

    let now = new Date().toISOString();

    let newComment = {
      user: Server.user.getId(),
      content: content,
      date: now
    };

    this.state.comments.push(newComment);
    this.sortComments(this.state.comments, this.state.sort);
    this.forceUpdate();

    let params = {
      id: this.state.blog.id,
      content: content
    };

    let response = await Server.blogs.createComment(params);

    if(response.success) {
      newComment.date = response.date;

      await Server.blogs.clearBlogCommentsCache(this.state.blog.id);

      if(this.state.sort)
        this.onJumpToBottom();
    }
    else {
      this.state.comments.pop();
      this.sortComments(this.state.comments, this.state.sort);
      this.forceUpdate();
      this.setState({alert: response.message})
    }
  }

  onEditComment(date:string) {
    this.setState({edit: date});
  }

  async onSaveComment(content:string) {
    this.setState({edit: null});

    if(content == null) 
      return;

    let originalComments = JSON.parse(JSON.stringify(this.state.comments));

    for(let i = 0; i < this.state.comments.length; i++) {
      if(this.state.comments[i].date == this.state.edit) {
        if(content == '')
          this.state.comments.splice(i, 1);
        else
          this.state.comments[i].content = content;
        break;
      }
    }

    let response = null;

    if(content == '') {
      response = await Server.blogs.deleteComment({
        id: this.state.blog.id, 
        date: this.state.edit
      });
    }
    else {
      response = await Server.blogs.editComment({
        id: this.state.blog.id,
        date: this.state.edit,
        content: content
      });
    }
  
    if(response.success) 
      await Server.blogs.clearBlogCommentsCache(this.state.blog.id);
    else  {
      this.setState({comments: originalComments});
      this.setState({alert: response.message})
    }
  }

  onEditBlog() {
    this.setState({navigate: '/blog/edit/' + this.state.blog.slug})
  }

  onJumpToComments() {
    document.getElementById('CommentsHeader')?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }

  onJumpToBottom() {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }

  onSort() {
    if(this.state.more) 
      this.loadComments(this.state.blog.id, !this.state.sort);
    else 
      this.sortComments(this.state.comments, !this.state.sort);

    this.setState({sort: !this.state.sort});
  }

  async onLoadMore() {
    await this.loadMoreComments();
  }

  onLogin() {
    this.getBlog();
  }

  onLogout() {
  }

  onShareBlog() {
    this.setState({question: 'Share this blog to the activity feed?'})
  }

  async onQuestionYes() {
    let title = this.state.blog.title;
    let author = Server.public.getProfile(this.state.blog.author).name;
    let description = getFirstParagraph(this.state.blog.content);
    let url = window.location.href;

    this.setState({message: 'Sharing...', question: ''});
    let raw = `<div class="ql-unfurl"><a style="text-decoration:none" class="ql-unfurl-anchor" href="${url}" target="_blank"><div class="ql-unfurl-container"><div class="ql-unfurl-text" contenteditable="false" suppresscontenteditablewarning="true"><div class="site-page-subtext">${author}</div><div><b>${title}</b></div><div>${description}</div></div></div></a></div>`
    let content = encodeURIComponent(raw);
    let response = await Server.activity.createPost(content);
    await Server.public.sleep(250);
    this.setState({message: ''});
    if(!response.success) 
      this.setState({alert: response.message});
  }

  renderCommentsHeader() {
    let count = this.state.comments.length;
    let label = `${count}${this.state.more ? '+' : ''} Comment${count > 1 ? 's' : ''}`;
    let headerId = 'CommentsHeader';

    return (
      <div className="site-page-header" id={headerId}>
        <div className="site-page-row" style={{justifyContent: 'space-between', userSelect: 'none'}}>
          <div>
            {label}
          </div>
          <div style={{display: 'flex', flexDirection: 'row', columnGap: '15px'}}>
            <div style={{cursor: 'pointer'}} onClick={()=>this.onJumpToBottom()}>
              Bottom&nbsp;<HiArrowUturnDown style={{fontSize: '0.9em', transform: 'translateY(2px)'}} />
            </div>
            <div style={{cursor: 'pointer'}} onClick={()=>this.onSort()}>
              Sort&nbsp;
              {this.state.sort ?
                <BsCaretUpFill style={{fontSize: '1.0em', transform: 'translateY(3px)'}} /> :
                <BsCaretDownFill style={{fontSize: '1.0em', transform: 'translateY(3px)'}} />
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderComments() {
    let divs = [];

    for (let i = 0; i < this.state.comments.length; i++) {
      let comment = this.state.comments[i];
      let action = comment.user == Server.user.getId() ? 'edit' : '';

      if(this.state.comments[i].date == this.state.edit) {
        divs.push(
          <PostPanel 
            key={Math.random()}
            placeholder="Enter comment..."
            edit={comment.content}
            onPost={(content:string)=>this.onSaveComment(content)}
            maxLength={1024}
          />
        )
      }
      else {
        divs.push(
          <ContentPanel 
            key={Math.random()}
            author={comment.user}
            date={comment.date}
            content={comment.content}
            reply={true}
            action={action}
            onAction={()=>this.onEditComment(comment.date)}
          />
        )
      }
    }

    return divs;
  }

  render() {
    if(this.state.navigate != '') 
      return <Navigate to={this.state.navigate} />;

    let blog = this.state.blog;
    if (!blog) {
      return (
        <div className="site-page">
          <PageEnvelope width="600px">
            <PageHeader text={this.state.header} navigate="/blogs" />
          </PageEnvelope>
        </div>
      )
    }

    let action = (blog.author == Server.user.getId()) ? 'edit' : ''

    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <div className="site-page-column">

            <PageHeader text="View Blog" navigate="/blogs">
              {this.state.comments.length > 0 &&
                <div style={{display: 'flex', cursor: 'pointer'}} onClick={()=>this.onJumpToComments()}>
                  Comments&nbsp;<HiArrowUturnDown style={{fontSize: '0.9em', transform: 'translateY(4px)'}} />
                </div>
              }
            </PageHeader>

            <ContentPanel
              author={blog.author}
              date={blog.date}
              title={blog.title}
              content={blog.content}
              action={action}
              onAction={()=>this.onEditBlog()}
              onShare={()=>this.onShareBlog()}
            />

            <PostPanel 
              placeholder="Enter comment..."
              onPost={(content:string)=>this.onCreateComment(content)}
              maxLength={1024}
            />

            {this.state.comments.length > 0 &&
              this.renderCommentsHeader()
            }

            {this.renderComments()}

            {this.state.more &&
              <button onClick={()=>this.onLoadMore()} style={{backgroundColor: 'var(--page-background-color)', border: '1px solid var(--panel-background-color)'}}>
                {this.state.loadingMore ? 'Loading...' : 'Load More Replies'}
              </button>
            }

            {this.state.comments.length > 2 &&
              <PostPanel 
                placeholder="Enter comment..."
                onPost={(content:string)=>this.onCreateComment(content)}
                maxLength={1024}
              />
            }
          </div>
        </PageEnvelope>

        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        <QuestionModal message={this.state.question} onYes={()=>this.onQuestionYes()} onNo={()=>this.setState({question: ''})} />
      </div>
    )
  }
}

export default BlogPage;